import { createSlice } from '@reduxjs/toolkit';

const buildEditData = (record) => {
  const initialData = {};
  if (!record) return initialData;

  Object.keys(record).forEach((key) => {
    if (['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'].includes(key) && record[key]) {
      try {
        initialData[key] = new Date(record[key]).toISOString().split('T')[0];
      } catch (e) {
        initialData[key] = record[key];
      }
    } else if (Array.isArray(record[key])) {
      initialData[key] = record[key].map((item) => ({ ...item }));
    } else {
      initialData[key] = record[key];
    }
  });

  return initialData;
};

const deepCopy = (data) => JSON.parse(JSON.stringify(data));

const initialState = {
  showCommentForm: false,
  comment: '',
  status: '',
  offerAmount: '',
  scheduledDate: '',
  scheduledTime: '',
  isSubmitting: false,
  commentError: '',
  timeSlots: [],
  isEditing: false,
  editData: {},
  initialEditData: {},
  isSaving: false,
  statusMessage: { type: '', text: '' },
  isAssigning: false,
  selectedCollectorForAssign: '',
  currentRecord: null,
  showUnsavedModal: false,
  modalDiscardAction: null,
  isRefreshingId: false,
};

const recordDetailsSlice = createSlice({
  name: 'recordDetails',
  initialState,
  reducers: {
    initializeRecord(state, action) {
      const record = action.payload;
      const initialData = buildEditData(record);

      state.currentRecord = record || null;
      state.editData = initialData;
      state.initialEditData = deepCopy(initialData);
      state.isAssigning = false;
      state.selectedCollectorForAssign = record?.assignedCollector?._id || '';
      state.isEditing = false;
      state.statusMessage = { type: '', text: '' };
      state.showUnsavedModal = false;
      state.modalDiscardAction = null;

      state.showCommentForm = false;
      state.comment = '';
      state.status = '';
      state.offerAmount = '';
      state.scheduledDate = '';
      state.scheduledTime = '';
      state.isSubmitting = false;
      state.commentError = '';
      state.timeSlots = [];
    },
    applyUpdatedRecord(state, action) {
      const record = action.payload;
      const initialData = buildEditData(record);
      state.currentRecord = record || null;
      state.editData = initialData;
      state.initialEditData = deepCopy(initialData);
    },
    setCurrentRecord(state, action) {
      state.currentRecord = action.payload;
    },
    setShowCommentForm(state, action) {
      state.showCommentForm = action.payload;
    },
    setComment(state, action) {
      state.comment = action.payload;
    },
    setStatus(state, action) {
      state.status = action.payload;
    },
    setOfferAmount(state, action) {
      state.offerAmount = action.payload;
    },
    setScheduledDate(state, action) {
      state.scheduledDate = action.payload;
    },
    setScheduledTime(state, action) {
      state.scheduledTime = action.payload;
    },
    setIsSubmitting(state, action) {
      state.isSubmitting = action.payload;
    },
    setCommentError(state, action) {
      state.commentError = action.payload;
    },
    setTimeSlots(state, action) {
      state.timeSlots = action.payload;
    },
    resetCommentForm(state) {
      state.showCommentForm = false;
      state.comment = '';
      state.status = '';
      state.offerAmount = '';
      state.scheduledDate = '';
      state.scheduledTime = '';
      state.isSubmitting = false;
      state.commentError = '';
      state.timeSlots = [];
    },
    setIsEditing(state, action) {
      state.isEditing = action.payload;
    },
    setEditData(state, action) {
      state.editData = action.payload;
    },
    updateEditField(state, action) {
      const { name, value } = action.payload;
      state.editData = { ...state.editData, [name]: value };
    },
    addMultiEntry(state, action) {
      const fieldName = action.payload;
      const newItems = state.editData[fieldName] ? [...state.editData[fieldName]] : [];
      newItems.push({ value: '' });
      state.editData = { ...state.editData, [fieldName]: newItems };
    },
    removeMultiEntry(state, action) {
      const { fieldName, index } = action.payload;
      const newItems = [...(state.editData[fieldName] || [])];
      newItems.splice(index, 1);
      state.editData = { ...state.editData, [fieldName]: newItems };
    },
    updateMultiEntry(state, action) {
      const { fieldName, index, value } = action.payload;
      const newItems = state.editData[fieldName] ? [...state.editData[fieldName]] : [];
      if (newItems[index]) {
        newItems[index] = { ...newItems[index], value };
      } else {
        newItems[index] = { value };
      }
      state.editData = { ...state.editData, [fieldName]: newItems };
    },
    setIsSaving(state, action) {
      state.isSaving = action.payload;
    },
    setStatusMessage(state, action) {
      state.statusMessage = action.payload;
    },
    setIsAssigning(state, action) {
      state.isAssigning = action.payload;
    },
    setSelectedCollectorForAssign(state, action) {
      state.selectedCollectorForAssign = action.payload;
    },
    setShowUnsavedModal(state, action) {
      state.showUnsavedModal = action.payload;
    },
    setModalDiscardAction(state, action) {
      state.modalDiscardAction = action.payload;
    },
    resetEditData(state) {
      state.editData = deepCopy(state.initialEditData);
    },
  },
});

export const {
  initializeRecord,
  applyUpdatedRecord,
  setCurrentRecord,
  setShowCommentForm,
  setComment,
  setStatus,
  setOfferAmount,
  setScheduledDate,
  setScheduledTime,
  setIsSubmitting,
  setCommentError,
  setTimeSlots,
  resetCommentForm,
  setIsEditing,
  setEditData,
  updateEditField,
  addMultiEntry,
  removeMultiEntry,
  updateMultiEntry,
  setIsSaving,
  setStatusMessage,
  setIsAssigning,
  setSelectedCollectorForAssign,
  setShowUnsavedModal,
  setModalDiscardAction,
  resetEditData,
} = recordDetailsSlice.actions;

export const selectRecordDetails = (state) => state.recordDetails;
export const selectRecordDetailsCurrent = (state) => state.recordDetails.currentRecord;
export const selectRecordDetailsEditData = (state) => state.recordDetails.editData;
export const selectRecordDetailsInitialEditData = (state) => state.recordDetails.initialEditData;
export const selectRecordDetailsIsEditing = (state) => state.recordDetails.isEditing;
export const selectRecordDetailsIsSaving = (state) => state.recordDetails.isSaving;
export const selectRecordDetailsStatusMessage = (state) => state.recordDetails.statusMessage;
export const selectRecordDetailsIsAssigning = (state) => state.recordDetails.isAssigning;
export const selectRecordDetailsSelectedCollector = (state) =>
  state.recordDetails.selectedCollectorForAssign;
export const selectRecordDetailsShowUnsavedModal = (state) =>
  state.recordDetails.showUnsavedModal;
export const selectRecordDetailsModalDiscardAction = (state) =>
  state.recordDetails.modalDiscardAction;

export const selectCommentShowForm = (state) => state.recordDetails.showCommentForm;
export const selectCommentText = (state) => state.recordDetails.comment;
export const selectCommentStatus = (state) => state.recordDetails.status;
export const selectCommentOfferAmount = (state) => state.recordDetails.offerAmount;
export const selectCommentScheduledDate = (state) => state.recordDetails.scheduledDate;
export const selectCommentScheduledTime = (state) => state.recordDetails.scheduledTime;
export const selectCommentIsSubmitting = (state) => state.recordDetails.isSubmitting;
export const selectCommentError = (state) => state.recordDetails.commentError;
export const selectCommentTimeSlots = (state) => state.recordDetails.timeSlots;

export default recordDetailsSlice.reducer;
